import type { MRT_ColumnFiltersState } from 'material-react-table'
import qs from 'qs'
import type { IntlShape } from 'react-intl'
import { formatEnumFacetLabel } from '../filterBuilders'
import { readStoredFilterValue } from '../filters/filterOperator'
import type { FilterOption } from '../mrtCollectionTypes'
import { isCommittableFilter } from '../mrtMapping'

/**
 * The URL param a link carries table filters in. A link carries this or the saved-view `?view=`, never
 * both - each replaces the table's filters outright, so together they would race.
 */
export const MRT_DEEP_LINK_PARAM = 'tableFilters'

/**
 * The pre-MRT filter param. A page whose MRT table is still behind a flag renders the old table for
 * anyone outside the rollout, and that table reads this one - so a link to such a page carries both
 * formats. Named here only so the MRT side can clear it; it goes when the last flag does.
 */
export const LEGACY_FILTER_PARAM = 'filters'

const isColumnFilter = (entry: unknown): entry is { id: string; value: unknown } =>
  typeof entry === 'object' && entry !== null && typeof (entry as { id?: unknown }).id === 'string'

/**
 * One column's filter as a link carries it. Always the plain "is" selection, since a link says what to
 * show rather than what to exclude - the operator dropdown is the user's to change afterwards.
 */
export const filterOn = (columnId: string, value: unknown): MRT_ColumnFiltersState[number] => ({
  id: columnId,
  value: { operator: 'is', value },
})

/**
 * Raw enum values as multi-select options, labelled the way an enum column's own filter picker labels
 * them, so a chip reads identically whether it was chosen or followed.
 */
export const enumOptions = (
  valuesIntlPrefix: string,
  values: string[],
  formatMessage: IntlShape['formatMessage'],
): FilterOption[] => {
  const formatLabel = formatEnumFacetLabel(valuesIntlPrefix, formatMessage)
  return values.map(value => ({ value, label: formatLabel(value) }))
}

/**
 * A link to a table, opening it on the given filters - and on no filters at all when given none. A link
 * that states a number has to land on a list showing that number, so an empty list is an instruction to
 * clear, not an absent one: the param is always carried.
 */
export const mrtDeepLinkParam = (columnFilters: MRT_ColumnFiltersState): Record<string, string> => ({
  [MRT_DEEP_LINK_PARAM]: JSON.stringify(columnFilters),
})

export const buildMrtDeepLink = (path: string, columnFilters: MRT_ColumnFiltersState): string =>
  `${path}?${qs.stringify(mrtDeepLinkParam(columnFilters), { encodeValuesOnly: true })}`

/**
 * The filters a link asks for, narrowed to those this page can honour: a column it has, and a value its
 * chip and query can both read. Anything else is dropped rather than stored, so a link written against an
 * older version of the table cannot leave a chip nothing can edit or remove.
 *
 * Returns undefined only when there is no link to follow, which leaves the table's own filters alone.
 * A link carrying an empty list returns one, which clears them.
 */
export const parseMrtDeepLink = (search: string, columnIds: string[]): MRT_ColumnFiltersState | undefined => {
  const raw = qs.parse(search.replace(/^\?/, ''))[MRT_DEEP_LINK_PARAM]
  if (typeof raw !== 'string') return undefined

  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return undefined

    return parsed
      .filter(isColumnFilter)
      .filter(({ id }) => columnIds.includes(id))
      .map(({ id, value }) => ({ id, value: readStoredFilterValue(value) }))
      .filter(({ value }) => isCommittableFilter(value))
  } catch {
    return undefined
  }
}
